// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hatch — the canvas renderer: the grid, the clues, and all the juice (fill pops, mistake
// shake + sparks, line-done clue dimming, the completion sweep). The LAYOUT is a pure
// function (computeLayout) so tests/layout.test.ts can prove every mode fits a phone with no
// overflow and cells stay a usable size, without a browser. Cells shrink to fit; a max cell
// size keeps a desktop board centred rather than sprawling (the outsize lesson).

import { PALETTE } from './palette';
import { FILLED, MARKED, type GameEvent, type GameState } from './game';

export interface Layout {
  cell: number;
  slot: number; // width/height of one clue number's slot
  gutterW: number; // left gutter for row clues
  gutterH: number; // top gutter for column clues
  originX: number; // top-left of the whole (gutter + grid) block
  originY: number;
  gridX: number; // top-left of the grid itself
  gridY: number;
  gridSize: number; // size in px of the square grid
  size: number; // cells per side
}

const CLUE_SLOT_FRAC = 0.56; // a clue number's slot as a fraction of a cell
const MAX_CELL = 44; // cap so a wide desktop board stays centred, not sprawling
const MIN_CELL = 12;

/**
 * Pure layout: fit a `size`×`size` grid plus its clue gutters into cssW×cssH. `rowClueMax` /
 * `colClueMax` are the longest clue lists (so the gutters are only as wide/tall as needed).
 */
export function computeLayout(cssW: number, cssH: number, size: number, rowClueMax: number, colClueMax: number): Layout {
  const rc = Math.max(1, rowClueMax);
  const cc = Math.max(1, colClueMax);
  // cell constrained by width and height: gutter is (clueMax * frac) cells wide/tall
  const fitW = cssW / (size + rc * CLUE_SLOT_FRAC);
  const fitH = cssH / (size + cc * CLUE_SLOT_FRAC);
  const cell = Math.max(MIN_CELL, Math.min(MAX_CELL, Math.floor(Math.min(fitW, fitH))));
  const slot = cell * CLUE_SLOT_FRAC;
  const gutterW = rc * slot;
  const gutterH = cc * slot;
  const gridSize = size * cell;
  const blockW = gutterW + gridSize;
  const blockH = gutterH + gridSize;
  const originX = Math.round((cssW - blockW) / 2);
  const originY = Math.round((cssH - blockH) / 2);
  return {
    cell,
    slot,
    gutterW,
    gutterH,
    originX,
    originY,
    gridX: originX + gutterW,
    gridY: originY + gutterH,
    gridSize,
    size,
  };
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
  size: number;
}

export class Board {
  private ctx: CanvasRenderingContext2D;
  private cssW = 0;
  private cssH = 0;
  private dpr = 1;
  private layout: Layout | null = null;
  private clueMax = { row: 1, col: 1 };
  private curSize = 0;

  // animation state
  private pops = new Map<number, number>(); // idx → age ms
  private flashes = new Map<number, number>(); // idx → remaining ms (mistake red)
  private lineFlash = new Map<string, number>(); // `r5`/`c3` → remaining ms
  private particles: Particle[] = [];
  private shakeMs = 0;
  private sweep = -1; // 0..1 when a board completes, else -1
  cursor = -1; // keyboard cursor cell index (-1 = hidden)

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
  }

  /** Re-measure the parent and size the backing store. Guards against a 0-size measurement. */
  resize(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    if (w < 2 || h < 2) return; // transient 0-size: ignore and retry next frame
    this.dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    this.cssW = w;
    this.cssH = h;
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.relayout();
  }

  private relayout(): void {
    if (this.cssW < 2 || this.curSize < 1) return;
    this.layout = computeLayout(this.cssW, this.cssH, this.curSize, this.clueMax.row, this.clueMax.col);
  }

  private syncBoard(state: GameState): void {
    const b = state.board;
    if (b.size === this.curSize) return;
    this.curSize = b.size;
    this.clueMax = {
      row: Math.max(1, ...b.rowClues.map((c) => c.length)),
      col: Math.max(1, ...b.colClues.map((c) => c.length)),
    };
    this.pops.clear();
    this.flashes.clear();
    this.lineFlash.clear();
    this.particles.length = 0;
    this.sweep = -1;
    this.relayout();
  }

  /** The cell index at a client point, or null if outside the grid. */
  hitTest(clientX: number, clientY: number): number | null {
    const L = this.layout;
    if (!L) return null;
    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left - L.gridX;
    const y = clientY - rect.top - L.gridY;
    if (x < 0 || y < 0 || x >= L.gridSize || y >= L.gridSize) return null;
    const c = Math.floor(x / L.cell);
    const r = Math.floor(y / L.cell);
    if (r < 0 || r >= L.size || c < 0 || c >= L.size) return null;
    return r * L.size + c;
  }

  /** Client-space centre of a cell (for the contrast probe / haptics). */
  cellCenterClient(idx: number): { x: number; y: number } | null {
    const L = this.layout;
    if (!L) return null;
    const rect = this.canvas.getBoundingClientRect();
    const r = Math.floor(idx / L.size);
    const c = idx % L.size;
    return { x: rect.left + L.gridX + (c + 0.5) * L.cell, y: rect.top + L.gridY + (r + 0.5) * L.cell };
  }

  moveCursor(dr: number, dc: number): void {
    if (!this.layout) return;
    const n = this.layout.size;
    if (this.cursor < 0) {
      this.cursor = Math.floor(n / 2) * n + Math.floor(n / 2);
      return;
    }
    let r = Math.floor(this.cursor / n) + dr;
    let c = (this.cursor % n) + dc;
    r = Math.max(0, Math.min(n - 1, r));
    c = Math.max(0, Math.min(n - 1, c));
    this.cursor = r * n + c;
  }

  cursorIdx(): number {
    if (this.cursor < 0) {
      this.moveCursor(0, 0);
    }
    return this.cursor;
  }

  onEvents(events: GameEvent[], reducedMotion: boolean): void {
    for (const e of events) {
      if (e.t === 'fill' && e.idx !== undefined) {
        this.pops.set(e.idx, 0);
        if (!reducedMotion) this.spawnSpark(e.idx, PALETTE.fillCore, 5);
      } else if (e.t === 'mistake' && e.idx !== undefined) {
        this.flashes.set(e.idx, 420);
        if (!reducedMotion) {
          this.shakeMs = Math.max(this.shakeMs, 260);
          this.spawnSpark(e.idx, PALETTE.bad, 9);
        }
      } else if (e.t === 'linedone' && e.line && e.index !== undefined) {
        this.lineFlash.set(`${e.line[0]}${e.index}`, 500);
      } else if (e.t === 'complete') {
        if (!reducedMotion) this.sweep = 0;
      }
    }
  }

  private spawnSpark(idx: number, color: string, n: number): void {
    if (!this.layout) return;
    const L = this.layout;
    const r = Math.floor(idx / L.size);
    const c = idx % L.size;
    const cx = L.gridX + (c + 0.5) * L.cell;
    const cy = L.gridY + (r + 0.5) * L.cell;
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n + Math.random() * 0.6;
      const sp = 0.04 + Math.random() * 0.09;
      this.particles.push({ x: cx, y: cy, vx: Math.cos(a) * sp * L.cell, vy: Math.sin(a) * sp * L.cell, life: 0, max: 320 + Math.random() * 220, color, size: L.cell * 0.09 });
    }
  }

  frame(state: GameState, opts: { dtMs: number; reducedMotion: boolean }): void {
    this.syncBoard(state);
    const L = this.layout;
    const ctx = this.ctx;
    const dt = opts.dtMs;

    // advance animations
    for (const [k, v] of this.pops) {
      const nv = v + dt;
      if (nv > 260) this.pops.delete(k);
      else this.pops.set(k, nv);
    }
    for (const [k, v] of this.flashes) {
      const nv = v - dt;
      if (nv <= 0) this.flashes.delete(k);
      else this.flashes.set(k, nv);
    }
    for (const [k, v] of this.lineFlash) {
      const nv = v - dt;
      if (nv <= 0) this.lineFlash.delete(k);
      else this.lineFlash.set(k, nv);
    }
    if (this.shakeMs > 0) this.shakeMs = Math.max(0, this.shakeMs - dt);
    if (this.sweep >= 0) {
      this.sweep += dt / 900;
      if (this.sweep > 1.4) this.sweep = -1;
    }
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life += dt;
      p.x += p.vx * (dt / 16.67);
      p.y += p.vy * (dt / 16.67);
      p.vy += 0.02 * (dt / 16.67) * (L ? L.cell : 20) * 0.02;
      if (p.life >= p.max) this.particles.splice(i, 1);
    }

    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.clearRect(0, 0, this.cssW, this.cssH);
    // subtle stage
    ctx.fillStyle = PALETTE.stage;
    ctx.fillRect(0, 0, this.cssW, this.cssH);

    if (!L) {
      ctx.restore();
      return;
    }

    let sx = 0;
    let sy = 0;
    if (this.shakeMs > 0) {
      const m = (this.shakeMs / 260) * L.cell * 0.16;
      sx = (Math.random() - 0.5) * m;
      sy = (Math.random() - 0.5) * m;
    }
    ctx.translate(sx, sy);

    this.drawBoard(ctx, L, state, opts.reducedMotion);
    this.drawClues(ctx, L, state);

    // particles
    for (const p of this.particles) {
      const a = 1 - p.life / p.max;
      ctx.globalAlpha = Math.max(0, a);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    ctx.restore();
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }

  private drawBoard(ctx: CanvasRenderingContext2D, L: Layout, state: GameState, reduced: boolean): void {
    const n = L.size;
    const gap = Math.max(1, Math.round(L.cell * 0.06));
    // board backing
    ctx.fillStyle = PALETTE.board;
    this.roundRect(ctx, L.gridX - gap, L.gridY - gap, L.gridSize + gap * 2, L.gridSize + gap * 2, Math.max(4, L.cell * 0.18));
    ctx.fill();

    const sweepX = this.sweep >= 0 ? L.gridX + this.sweep * L.gridSize : -1;

    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        const idx = r * n + c;
        const cell = state.cells[idx];
        const x = L.gridX + c * L.cell + gap / 2;
        const y = L.gridY + r * L.cell + gap / 2;
        const w = L.cell - gap;
        const rad = Math.max(2, L.cell * 0.16);

        const flash = this.flashes.get(idx);
        if (cell === FILLED) {
          let pop = 1;
          const age = this.pops.get(idx);
          if (age !== undefined && !reduced) {
            const t = age / 260;
            pop = 1 + Math.sin(Math.min(1, t) * Math.PI) * 0.16; // overshoot then settle
          }
          const cx = x + w / 2;
          const cy = y + w / 2;
          const pw = w * pop;
          const grad = ctx.createLinearGradient(0, cy - pw / 2, 0, cy + pw / 2);
          grad.addColorStop(0, PALETTE.fillCore);
          grad.addColorStop(0.55, PALETTE.fill);
          grad.addColorStop(1, PALETTE.fillEdge);
          ctx.fillStyle = grad;
          this.roundRect(ctx, cx - pw / 2, cy - pw / 2, pw, pw, rad);
          ctx.fill();
        } else {
          // unknown or marked → the empty-square surface
          ctx.fillStyle = flash ? PALETTE.bad : PALETTE.cellEmpty;
          this.roundRect(ctx, x, y, w, w, rad);
          ctx.fill();
          if (cell === MARKED) {
            ctx.strokeStyle = PALETTE.markGlyph;
            ctx.lineWidth = Math.max(1.5, L.cell * 0.08);
            ctx.lineCap = 'round';
            const m = w * 0.28;
            ctx.beginPath();
            ctx.moveTo(x + m, y + m);
            ctx.lineTo(x + w - m, y + w - m);
            ctx.moveTo(x + w - m, y + m);
            ctx.lineTo(x + m, y + w - m);
            ctx.stroke();
          }
        }
        // completion sweep highlight
        if (sweepX >= 0 && cell === FILLED) {
          const dist = Math.abs(x + w / 2 - sweepX);
          if (dist < L.cell * 1.2) {
            ctx.globalAlpha = (1 - dist / (L.cell * 1.2)) * 0.8;
            ctx.fillStyle = '#ffffff';
            this.roundRect(ctx, x, y, w, w, rad);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
        }
      }
    }

    // bold separators every 5 cells (nonogram convention)
    if (n > 5) {
      ctx.strokeStyle = PALETTE.gridline;
      ctx.lineWidth = 2;
      for (let i = 5; i < n; i += 5) {
        ctx.beginPath();
        ctx.moveTo(L.gridX + i * L.cell, L.gridY);
        ctx.lineTo(L.gridX + i * L.cell, L.gridY + L.gridSize);
        ctx.moveTo(L.gridX, L.gridY + i * L.cell);
        ctx.lineTo(L.gridX + L.gridSize, L.gridY + i * L.cell);
        ctx.stroke();
      }
    }

    // keyboard cursor ring
    if (this.cursor >= 0 && this.cursor < n * n) {
      const r = Math.floor(this.cursor / n);
      const c = this.cursor % n;
      ctx.strokeStyle = PALETTE.spark;
      ctx.lineWidth = Math.max(2, L.cell * 0.09);
      this.roundRect(ctx, L.gridX + c * L.cell + 1, L.gridY + r * L.cell + 1, L.cell - 2, L.cell - 2, Math.max(3, L.cell * 0.18));
      ctx.stroke();
    }
  }

  private drawClues(ctx: CanvasRenderingContext2D, L: Layout, state: GameState): void {
    const b = state.board;
    const n = L.size;
    const fs = Math.max(9, Math.round(L.cell * 0.46));
    ctx.font = `700 ${fs}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // row clues — right-aligned into the left gutter
    for (let r = 0; r < n; r++) {
      const clue = b.rowClues[r];
      const done = state.rowDone[r];
      const flash = this.lineFlash.get(`r${r}`);
      const cy = L.gridY + (r + 0.5) * L.cell;
      const list = clue.length ? clue : [0];
      for (let k = 0; k < list.length; k++) {
        const cx = L.gridX - (list.length - k - 0.5) * L.slot;
        this.drawClueNumber(ctx, String(list[k]), cx, cy, done, clue.length === 0, flash);
      }
    }
    // column clues — bottom-aligned into the top gutter
    for (let c = 0; c < n; c++) {
      const clue = b.colClues[c];
      const done = state.colDone[c];
      const flash = this.lineFlash.get(`c${c}`);
      const cx = L.gridX + (c + 0.5) * L.cell;
      const list = clue.length ? clue : [0];
      for (let k = 0; k < list.length; k++) {
        const cy = L.gridY - (list.length - k - 0.5) * L.slot;
        this.drawClueNumber(ctx, String(list[k]), cx, cy, done, clue.length === 0, flash);
      }
    }
  }

  private drawClueNumber(ctx: CanvasRenderingContext2D, s: string, cx: number, cy: number, done: boolean, empty: boolean, flash: number | undefined): void {
    if (flash) {
      ctx.globalAlpha = 0.25 + 0.5 * (flash / 500);
      ctx.fillStyle = PALETTE.clueDone;
      ctx.fillText(s, cx, cy);
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = done ? PALETTE.clueDone : empty ? PALETTE.inkDim : PALETTE.ink;
    ctx.globalAlpha = done ? 0.7 : 1;
    ctx.fillText(s, cx, cy);
    ctx.globalAlpha = 1;
    if (done && !empty) {
      // strikethrough — a shape cue, not colour alone
      const w = ctx.measureText(s).width;
      ctx.strokeStyle = PALETTE.clueDone;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx - w / 2 - 1, cy);
      ctx.lineTo(cx + w / 2 + 1, cy);
      ctx.stroke();
    }
  }

  destroy(): void {
    this.particles.length = 0;
    this.pops.clear();
    this.flashes.clear();
    this.lineFlash.clear();
  }
}
